import type { StateCreator } from 'zustand';
import { leadGenApi, type CallTableRow } from '../services/leadGenApi';

// Power Dialer (past-plans/anrufen-power-dialer-spec.md Teil 3): reiner Zustand — die eigentliche
// Wähl-Sequenz (useTrackedDial, Timer, Events) läuft in `PowerDialerRunner.tsx`, weil sie React-
// Hooks braucht, die ein Zustand-Store nicht hat. Diese Slice ist nur das Gedächtnis dazwischen:
// wo steht der Dialer, wer ist dran, was ist als Nächstes.
//
// `phase`:
//   'bereit'   — der Runner darf den aktuellen Warteschlangen-Platz wählen
//   'waehlt'   — Mikro-Tor + CloudTalk-Aufruf laufen gerade
//   'wartet'   — geklingelt, wartet auf „Verbunden"/„Mailbox"/„Niemand" (25s-Fenster, E7)
//   'verbunden'— der Setter übernimmt, Cockpit läuft normal weiter
//   'countdown'— zwischen zwei Anrufen, E12
//   'pausiert' — Setter hat pausiert, kein Auto-Weiter
//   'fehler'   — vorAnruf ist gescheitert, Zeile wird übersprungen (kein Log, E-Teil 3 „Fehler:…")
export type PowerDialerPhase =
  | 'bereit' | 'waehlt' | 'wartet' | 'verbunden' | 'countdown' | 'pausiert' | 'fehler';

export interface PowerDialerSlice {
  dialerAktiv: boolean;
  dialerWarteschlange: CallTableRow[];
  dialerIndex: number;
  dialerPhase: PowerDialerPhase;
  dialerFehler: string | null;
  // E2: „Trotzdem anrufen" gilt für die ganze Sitzung, nicht pro Anruf.
  dialerMikroUebersprungen: boolean;
  // Date.now()-Zielzeit für den Countdown zwischen zwei Anrufen (E12) bzw. das 25s-Warten (E7).
  dialerZielzeit: number | null;

  dialerStarten: (warteschlange: CallTableRow[]) => void;
  dialerBeenden: () => void;
  dialerSetzePhase: (phase: PowerDialerPhase, zielzeit?: number | null) => void;
  dialerNaechster: () => void;
  dialerSetzeFehler: (nachricht: string | null) => void;
  dialerMerkeMikroBestaetigung: () => void;
}

export const createPowerDialerSlice: StateCreator<PowerDialerSlice> = (set, get) => ({
  dialerAktiv: false,
  dialerWarteschlange: [],
  dialerIndex: 0,
  dialerPhase: 'bereit',
  dialerFehler: null,
  dialerMikroUebersprungen: false,
  dialerZielzeit: null,

  dialerStarten: (warteschlange) => set({
    dialerAktiv: true, dialerWarteschlange: warteschlange, dialerIndex: 0, dialerPhase: 'bereit',
    dialerFehler: null, dialerMikroUebersprungen: false, dialerZielzeit: null,
  }),

  dialerBeenden: () => set({
    dialerAktiv: false, dialerWarteschlange: [], dialerIndex: 0, dialerPhase: 'bereit',
    dialerFehler: null, dialerZielzeit: null,
  }),

  dialerSetzePhase: (phase, zielzeit = null) => set({ dialerPhase: phase, dialerZielzeit: zielzeit }),

  dialerNaechster: () => {
    const { dialerIndex, dialerWarteschlange } = get();
    const naechster = dialerIndex + 1;
    if (naechster >= dialerWarteschlange.length) {
      // Warteschlange durch — Sitzung endet von selbst, nicht mit Fehler. Review-Fund (bestätigt):
      // die Backend-Sperre (services/power_dialer.py) muss auch hier explizit freigegeben werden,
      // nicht nur beim manuellen „Beenden"-Klick — sonst blockiert sie andere Setter bis zum
      // TTL-Ablauf (60s), obwohl der Dialer hier längst fertig ist.
      set({ dialerAktiv: false, dialerPhase: 'bereit', dialerZielzeit: null });
      void leadGenApi.powerDialerStop();
      return;
    }
    set({ dialerIndex: naechster, dialerPhase: 'bereit', dialerFehler: null, dialerZielzeit: null });
  },

  dialerSetzeFehler: (nachricht) => set({ dialerFehler: nachricht, dialerPhase: nachricht ? 'fehler' : 'bereit' }),

  dialerMerkeMikroBestaetigung: () => set({ dialerMikroUebersprungen: true }),
});
